<template>
	<div :class="classesProcessed" :id="'table-' + _uid">
		<div class="table-search" v-if="!hideSearch && searchInputs.length > 0">
			<h3 v-html="searchTitle" v-if="searchTitle !== ''" class="search-title" />

			<slot name="after-title"></slot>

			<span v-if="showSimpleSearch">
				<span
					v-for="(searchInput, searchInputIndex) in searchInputs"
					:key="(name || 'table-input') + '-searchInput-' + searchInputIndex"
					:class="{
						'input-wrap': true,
						dates: searchInput.type === 'date-range',
						number: searchInput.type === 'number',
						button: searchInput.type === 'button',
						switcher: searchInput.type.toLowerCase() === 'switcher' || searchInput.type.toLowerCase() === 'toggle',
					}">
					<label
						v-if="
							searchInput.showLabel ||
							(searchInput.showLabelWhenFilled &&
								search[searchInput.var] &&
								((Array.isArray(search[searchInput.var]) && search[searchInput.var].length > 0) ||
									(Array.isArray(search[searchInput.var]) === false &&
										search[searchInput.var] !== '' &&
										search[searchInput.var] !== null)))
						">
						{{ searchInput.showLabel ? searchInput.showLabel : searchInput.placeholder }}
						<Tip position="fromCenter" v-if="searchInput.tip">{{ searchInput.tip }}</Tip>
					</label>

					<input
						:type="searchInput.type"
						:placeholder="searchInput.placeholder"
						v-model="search[searchInput.var]"
						v-if="['text', 'number'].indexOf(searchInput.type) >= 0"
						@keypress.enter="searchTable" />

					<span
						class="large-button"
						@click="$emit(searchInput.click, searchInput.val)"
						v-html="searchInput.label"
						v-if="searchInput.type === 'button'" />

					<span
						class="large-button"
						@click="$emit(searchInput.click, searchInput.val)"
						v-html="searchInput.label"
						v-if="searchInput.type === 'button'" />

					<Switcher
						:switcherLabel="searchInput.label"
						:switcherLabel2="searchInput.label2"
						:sameColour="true"
						v-model="search[searchInput.var]"
						v-if="searchInput.type.toLowerCase() === 'switcher' || searchInput.type.toLowerCase() === 'toggle'" />

					<v-select
						:placeholder="searchInput.placeholder"
						v-model="search[searchInput.var]"
						:options="searchInput.items"
						:reduce="(option) => option.value"
						v-if="searchInput.type === 'select'"
						:multiple="searchInput.multi ? true : false"
						:clearable="typeof searchInput.clearable !== 'undefined' ? searchInput.clearable : true"></v-select>

					<DateFilter @change="changeDate(searchInput.var, $event)" :fixed="true" v-if="searchInput.type === 'date-range'" />
				</span>
			</span>

			<div class="search-actions" v-if="showSimpleSearch">
				<font-awesome-icon icon="search" class="mag" @click="searchTable" :style="{ color: 'black' }" />

				<font-awesome-icon icon="file-csv" class="csv" @click="doDownload('csv')" :style="{ color: 'black' }" v-if="showCSV" />

				<font-awesome-icon icon="file-pdf" class="pdf" @click="doDownload('pdf')" :style="{ color: 'black' }" v-if="showPDF" />

				<font-awesome-icon
					icon="floppy-disk"
					class="save"
					@click="saveSearch"
					:style="{ color: 'black' }"
					title="Save Search"
					v-if="showSave" />

				<font-awesome-icon
					icon="folder-open"
					class="save"
					@click="showSavedSearches"
					:style="{ color: 'black' }"
					title="Saved Searches"
					v-if="savedSearches.length > 0" />

				<span v-if="(hasSearches || searched) && !searchMultiple" @click="clearSearch" class="clearSearch">Clear</span>
			</div>

			<div :class="{ 'search-actions': true, multiple: true, 'has-simple': showSimpleSearch }" v-if="searchMultiple">
				<span @click="doSearchMultiple">
					<font-awesome-icon icon="circle-plus" :style="{ color: 'black' }" />
					<span v-html="searchMultipleButtonLabelInternal" />
				</span>
			</div>
		</div>

		<TableFilterQuery
			:searchQuery="searchQuery"
			:orderBySelected="orderBySelected"
			:searchSetup="searchSetupMultipleProcessed"
			:hasOrderBy="hasOrderBy"
			:minimal="true"
			:showClear="true"
			:title="filterQueryTitle"
			:descriptionOverride="filterQueryDescriptionOverride"
			:class="{ 'the-query': true, 'hide-query': tempHideFilter }"
			v-if="searchMultiple && searchMultipleValuesCompd.query && searchMultipleValuesCompd.query.length > 0"
			@clicked="doSearchMultiple"
			@clearAll="clearFromFilter" />

		<slot name="after-filter-query"></slot>

		<div :class="{ 'show-headings-wrap': true }" v-if="!hideHeadingShowHide">
			<div
				:class="{
					'show-headings': true,
					'show-filters': showHeadingFiltersInternal,
				}"
				v-if="name&&!hideHeadingShowHide">
				<h2 class="show-headings-heading" @click="toggleHeadingFilters" v-html="toggleHeadingsButtonLabel" />

				<div
					class="show-headings-wrap"
					v-for="(headingsToShow2, headingsToShowIndex) in headingsToShow"
					:key="(name || 'table-input') + '-headings-to-show-' + headingsToShowIndex">
					<h3 class="show-headings-sub-heading" v-if="headingsToShow.length > 1" v-html="headingsToShow2.type" />

					<FormInput
						type="checkbox"
						:key="(name || 'table-input') + '--' + headingsToShowIndex"
						:preName="name || 'table-input'"
						:inline="true"
						:disabled="showHeadings.length < 3"
						v-model="showHeadings"
						:items="headingsToShow2.headings"
						:labelStyles="{ color: '#fff', opacity: '1' }" />
				</div>
			</div>

			<h3 class="show-headings-heading-breakdown" v-if="hiddenHeadingInfo && !showHeadingFiltersInternal" v-html="hiddenHeadingInfo" />
		</div>

		<div class="table-mobile" v-if="showTable">
			<h2 :class="{ 'table-title': true, show: title !== '' }" v-html="title" />

			<div v-if="hasCheckbox >= 0" class="select-all">
				<input type="checkbox" v-model="selectAll" :id="id + '-select-check-heading-' + hasCheckbox" /><label
					:for="id + '-select-check-heading-' + hasCheckbox"
					>Select All</label
				>
			</div>

			<div :class="[{ 'table-item': true }, trClasses(item)]" v-for="(item, itemIndex) in processedItems" :key="'row-mobile-' + item.index">
				<div v-for="(cell, cellIndex) in item" :key="(name || 'table-input') + '-mobile-item-' + cellIndex" class="table-item-wrap">
					<div
						:class="[{ 'table-cell': true }, cell.mobileClass]"
						v-if="
							((!cell.checkBoxVar && (cell.value || cell.value === 0)) || cell.checkBox) &&
							(!cell.mobileShowMoreOnly || showingMore(itemIndex)) & !headings[cell.headingVar].hide
						">
						<h3 class="cell-heading" v-html="headings[cell.headingVar].value" v-if="!cell.hideHeadingMobile" />

						<h4
							:class="{
								'cell-value': true,
								checkbox: cell.checkBox,
							}">
							<span
								v-if="typeof cell.click !== 'undefined' && cell.type === 'normal' && !cell.checkBox"
								class="clickable"
								@click="clickCell(cell)"
								v-html="cell.value" />

							<span v-if="typeof cell.click === 'undefined' && cell.type === 'normal' && !cell.checkBox" v-html="cell.value" />

							<input
								type="checkbox"
								v-model="selected[cell.checkBoxVar]"
								v-if="cell.checkBox"
								:id="id + '-select-check-mobile-' + itemIndex" /><label
								v-if="cell.checkBox"
								:for="id + '-select-check-mobile-' + itemIndex"
								v-html="'&nbsp;'" />

							<span class="discs" v-if="cell.type === 'discs' && cell.discs.length > 0">
								<span
									v-for="(disc, discIndex) in cell.discs"
									:key="discIndex"
									:class="[{ disc: true }, disc.class]"
									:style="disc.style ? disc.style : ''"
									>{{ disc.value }}</span
								>
							</span>

							<span
								v-if="cell.type === 'discs' && typeof cell.click !== 'undefined' && typeof cell.label !== 'undefined'"
								:class="[
									{
										edit: true,
										show: cell.discs.length === 0,
										'has-discs': cell.discs.length > 0,
									},
								]"
								@click="clickCell(cell)"
								><span v-html="cell.label"
							/></span>

							<component
								:is="cell.component"
								:settings="cell.value"
								:hideIcons="hideIcons"
								:tableMeta="meta"
								v-if="cell.type === 'component'"
								@clicked="clicked($event, cell.var, cell.value)"
								@clicked2="clicked($event, cell.var, cell.value, 2)"
								@clicked3="clicked($event, cell.var, cell.value, 3)" />
						</h4>
					</div>
				</div>

				<span class="show-more" v-if="hasShowMore && !showingMore(itemIndex)" @click="doShowMore(itemIndex)">Show More</span>

				<span class="show-more" v-if="hasShowMore && showingMore(itemIndex)" @click="doShowMore(itemIndex, true)">Show Less</span>
			</div>

			<v-select
				:placeholder="'Filter'"
				v-model="checkboxFilter"
				:options="checkboxOptions"
				:reduce="(option) => option._id"
				v-if="checkboxOptions.length > 0 && hasOptionsSelected"></v-select>

			<Pagination
				v-if="thePages > 1"
				:thePages="thePages"
				:thePage="thePage"
				:page="page"
				:pageClassRules="pageClassRules"
				:doLoadMore="doLoadMore"
				@changePage="changePage"
				@loadMore="loadMore"
				:isMobile="true" />

			<div v-if="processedItems.length === 0 && emptyLabelProcessed" class="empty-wrap">
				<span class="empty" v-html="emptyLabelProcessed" />
			</div>

			<div class="add-button" v-if="addNewButtonLabel">
				<div class="center">
					<span class="large-button" @click="addNew" v-html="addNewButtonLabel" />
				</div>
			</div>

			<div class="add-button" v-if="baseButtonSettings">
				<div :colspan="colspan" class="center">
					<BaseButton v-if="baseButtonSettings" :settings="baseButtonSettings" @clicked="baseButtonClick" />
				</div>
			</div>
		</div>

		<h2 class="table-title" v-html="title" v-if="title !== '' && showTable" />

		<div
			:class="{
				'table-wrap-sticky': true,
				sticky: stickyOption,
				active: stickyOn,
			}"
			v-if="showTable">
			<span :class="{ 'sticky-btn': true, active: stickyOn }" v-if="stickyOption" @click="stickyOn = !stickyOn"
				><span>Expand table</span><font-awesome-icon icon="down-left-and-up-right-to-center" v-if="stickyOn"
			/></span>

			<table>
				<thead>
					<tr>
						<th class="pad">&nbsp;</th>
						<th
							v-for="(heading, headingIndex) in processedHeadings"
							:key="(name || 'table-input') + '-heading-index-' + headingIndex"
							:class="headingClasses2[heading.var]"
							:style="headingStyles(headingIndex)">
							<component
								:is="heading.headingComponent"
								:settings="heading.settings"
								:tableMeta="meta"
								@clicked="clickHeader(heading)"
								v-if="!!heading.headingComponent" />

							<span
								:class="{ cell: true, hasTip: !!heading.tip }"
								@click="clickHeader(heading)"
								v-if="!heading.headingComponent && !heading.checkBox"
								>{{ heading.hideHeadingDesktop ? "" : heading.value
								}}<Tip
									:position="headingIndex > 2 ? 'fromRight' : 'fromLeft'"
									:class="{ hideQ: true }"
									v-if="heading.tip && heading.tip !== ''"
									>{{ heading.tip }}</Tip
								></span
							>

							<span v-if="heading.checkBox">
								<input type="checkbox" v-model="selectAll" :id="id + '-select-check-heading-' + headingIndex" /><label
									v-if="heading.checkBox"
									:for="id + '-select-check-heading-' + headingIndex" />
							</span>

							<select
								v-if="!heading.headingComponent && heading.filters"
								v-model="filters[heading.var]"
								:name="'filter-' + heading.var">
								<option
									v-for="(filter, filterIndex) in heading.filters"
									:key="filterIndex"
									:value="filter.value"
									:selected="selectedSelect(heading.var, filter.value, filterIndex)">
									{{ filter.label }}
								</option>
							</select>
						</th>
						<th class="pad">&nbsp;</th>
					</tr>
				</thead>

				<tbody>
					<tr v-for="(item, itemIndex) in processedItems" :key="(name || 'table-') + '-row-' + item.index" :class="trClasses(item)">
						<td class="pad">&nbsp;</td>

						<td
							v-for="(cell, cellIndex) in item"
							:key="(name || 'table-') + 'desktop-item-' + cellIndex"
							:class="cellClasses(cell)"
							:colspan="cell.colspan">
							<span v-if="typeof cell.href !== 'undefined' && cell.type === 'normal' && !cell.checkBox" class="clickable cell">
								<a
									:href="cell.href"
									:target="cell.target ? cell.target : '_self'"
									:title="cell.title ? cell.title : ''"
									@click="clickCell(cell)"
									v-html="cell.value" />
							</span>

							<span
								v-if="
									typeof cell.click !== 'undefined' && typeof cell.href === 'undefined' && cell.type === 'normal' && !cell.checkBox
								"
								class="clickable cell"
								@click="clickCell(cell)"
								v-html="cell.value" />

							<span v-if="cell.checkBox">
								<input
									type="checkbox"
									v-model="selected[cell.checkBoxVar]"
									v-if="cell.checkBox"
									:id="id + '-select-check-table-' + itemIndex" /><label
									v-if="cell.checkBox"
									:for="id + '-select-check-table-' + itemIndex" />
							</span>

							<span
								v-if="
									typeof cell.click === 'undefined' && typeof cell.href === 'undefined' && cell.type === 'normal' && !cell.checkBox
								"
								v-html="cell.value"
								:title="cell.title || ''"
								class="cell" />

							<span class="discs" v-if="cell.type === 'discs' && cell.discs.length > 0">
								<span
									v-for="(disc, discIndex) in cell.discs"
									:key="discIndex"
									:class="[{ disc: true }, disc.class]"
									:style="disc.style ? disc.style : ''"
									>{{ disc.value }}</span
								>
							</span>
							<span
								v-if="cell.type === 'discs' && typeof cell.click !== 'undefined' && typeof cell.label !== 'undefined'"
								:class="[
									{
										edit: true,
										show: cell.discs.length === 0,
										'has-discs': cell.discs.length > 0,
									},
								]"
								@click="clickCell(cell)">
								<span v-html="cell.label" />
							</span>

							<component
								:is="cell.component"
								:settings="cell.value"
								:hideIcons="hideIcons"
								:tableMeta="meta"
								v-if="cell.type === 'component'"
								@clicked="clicked($event, cell.var, cell.value)" />
						</td>

						<td class="pad">
							&nbsp;
							<div class="html2pdf__page-break" v-if="(itemIndex + 1) % 9 === 0" />
						</td>
					</tr>

					<tr class="pagination" v-if="thePages > 1 && !hideThePagination">
						<td :colspan="colspan" class="center">
							<Pagination
								:thePages="thePages"
								:thePage="thePage"
								:page="page"
								:pageClassRules="pageClassRules"
								:doLoadMore="doLoadMore"
								:loading="loading"
								:loadingText="loadingText"
								@changePage="changePage"
								@loadMore="loadMore" />
						</td>
					</tr>

					<tr v-if="processedItems.length === 0 && emptyLabelProcessed" class="no-bg">
						<td :colspan="colspan" class="center">
							<span class="empty" v-html="emptyLabelProcessed" />
						</td>
					</tr>

					<tr class="filter" v-if="checkboxOptions.length > 0 && hasOptionsSelected">
						<td :colspan="colspan" class="center">
							<v-select
								:placeholder="'Filter'"
								v-model="checkboxFilter"
								:options="checkboxOptions"
								:reduce="(option) => option._id"></v-select>
						</td>
					</tr>

					<tr class="add-button" v-if="addNewButtonLabel">
						<td :colspan="colspan" class="center">
							<span class="large-button" @click="addNew" v-html="addNewButtonLabel" />
						</td>
					</tr>

					<tr class="add-button" v-if="baseButtonSettings">
						<td :colspan="colspan" class="center">
							<BaseButton v-if="baseButtonSettings" :settings="baseButtonSettings" @clicked="baseButtonClick" />
						</td>
					</tr>
				</tbody>
			</table>
		</div>

		<TableFilter
			v-if="showTableSearch || initialLoad"
			:searchSetup="searchSetupMultipleProcessed"
			:groupSetup="searchMultipleGroups"
			:comparisonLabels="searchMultipleComparisons"
			:title="searchMultipleTitleCompd"
			:description="searchMultipleDescriptionCompd"
			:toolTipText="searchMultipleToolTipText"
			:toolTipPreText="searchMultipleToolTipPreText"
			:values="searchMultipleValuesCompd"
			:name="searchMultipleType"
			:selectOptions="searchMultipleSelects"
			:maxLines="searchMultipleMaxLines"
			:addAnotherLabel="searchMultipleAddAnotherLabel"
			:orderByList="searchMultipleOrderByList"
			:orderByDefault="searchMultipleOrderByDefault"
			:orderByMax="searchMultipleOrderByMax"
			:orderByDefaultDirection="searchMultipleOrderByDefaultDirection"
			:initialLoad="initialLoad"
			:hiddenByDefault="searchMultipleHiddenByDefault"
			:showSaveFilter="showSaveFilter"
			:meta="meta"
			:key="queryKey"
			@close="closeTableFilter"
			@changed="filtersChanged"
			@saveFilter="saveFilter" />

		<SaveSearchModal
			v-if="showSaveSearchModal"
			@close="showSaveSearchModal = false"
			@saved="loadSavedSearches"
			:search="searchObject"
			:sort="sorted"
			:savedSearchType="getSavedSearchType"
			:searches="savedSearches" />

		<SavedSearchesModal
			v-if="showSavedSearchesModal"
			@close="showSavedSearchesModal = false"
			@run="runSavedSearch"
			@delete="deleteSavedSearch"
			:savedSearchType="getSavedSearchType"
			:searches="savedSearches" />
	</div>
</template>

<script>
import Vue from "vue";
import ApiService from "./../services/api.service";
import { mapMutations, mapState } from "vuex";
const { TokenService } = require("./../services/storage.service");
import general from "./../utils/general.js";
import BaseButton from "./BaseButton.vue";
import DateFilter from "./Data/DateFilter.vue";
import Pagination from "./Display/Pagination.vue";
import TableFilterQuery from "./TableFilterQuery.vue";
import Switcher from "./Switcher.vue";
import Tip from "./Tip.vue";
import TableFilter from "./TableFilter.vue";
import FormInput from "./Form/Input.vue";
import SaveSearchModal from "./Display/SaveSearchModal.vue";
import SavedSearchesModal from "./Display/SavedSearchesModal.vue";
export default {
	name: "Table",
	components: {
		BaseButton,
		DateFilter,
		Switcher,
		Tip,
		TableFilter,
		FormInput,
		Pagination,
		TableFilterQuery,
		SaveSearchModal,
		SavedSearchesModal,
	},
	props: {
		headings: [Object, Array],
		items: Array,
		stickyOption: {
			type: Boolean,
			default: true,
		},
		hasInternalFiltering: {
			type: Boolean,
			default: false,
		},
		showTable: {
			type: Boolean,
			default: true,
		},
		enableHeadingFilters: {
			type: Boolean,
			default: true,
		},
		classes: {
			type: Array,
			default: function () {
				return [];
			},
		},
		count: {
			type: Number,
			default: 1,
		},
		pages: {
			type: Number,
			default: 0,
		},
		pageClassRules: {
			type: Array,
			default: () => [],
		},
		triggerSomething: {
			type: String,
			default: "",
		},
		page: {
			type: [Number, String],
			default: 0,
		},
		emptyLabel: {
			type: String,
			default: "No items",
		},
		emptySearch: {
			type: String,
			default: "No results",
		},
		emptyCell: {
			type: String,
			default: "&nbsp;",
		},
		loadingText: {
			type: String,
			default: "Loading...",
		},
		more: {
			type: Number,
			default: 0,
		},
		loading: {
			type: Boolean,
			default: false,
		},
		hideSearch: {
			type: Boolean,
			default: false,
		},
		hideHeadingShowHide: {
			type: Boolean,
			default: false,
		},
		hideIcons: {
			type: Boolean,
			default: false,
		},
		hidePagination: {
			type: Boolean,
			default: false,
		},
		sorted: {
			type: Object,
			default: function () {
				return {};
			},
		},
		meta: {
			type: Object,
			default: function () {
				return {};
			},
		},
		id: {
			type: String,
			default: "t0",
		},
		title: {
			type: String,
			default: "",
		},
		searchTitle: {
			type: String,
			default: "",
		},
		searchSetup: {
			type: Object,
			default: () => {
				return {};
			},
			/*
      eg
      { 
        searchText: { type: 'text', placeholder: '', showLabel: 'Surname, First Name or Email' },
        approved: { type: 'select', showLabel: 'Approved', items: [{ value: false, label: 'No' },{ value: true, label: 'Yes' }], multi: false }
      }
      emits to @search on table 
      */
		},
		searchSetupMultiple: {
			type: Object,
			default: () => {
				return {};
			},
			/*
      eg
      { 
        searchText: { type: 'text', placeholder: '', showLabel: 'Surname, First Name or Email' },
        approved: { type: 'select', showLabel: 'Approved', items: [{ value: false, label: 'No' },{ value: true, label: 'Yes' }], multi: false }
      }
      emits to @search on table 
      */
		},
		searchMultiple: {
			type: Boolean,
			default: false,
		},
		searchMultipleMaxLines: {
			type: Number,
			default: 0,
		},
		searchMultipleTitle: {
			type: String,
			default: "Search",
		},
		searchMultipleButtonLabel: {
			type: String,
			default: "Click to add filter options",
		},
		searchMultipleLastUpdate: {
			type: Number,
			default: 0,
		},
		searchMultipleGroups: {
			type: Object,
			default: () => {
				return {};
			},
		},
		searchMultipleComparisons: {
			type: Object,
			default: () => {
				return {};
			},
		},
		searchMultipleSelects: {
			type: Object,
			default: () => {
				return {};
			},
		},
		searchMultipleValues: {
			type: [Object, Array],
			default: () => {
				return [];
			},
		},
		searchMultipleAddAnotherLabel: {
			type: String,
			default: "Add another filter group",
		},
		searchMultipleDescription: {
			type: String,
			default: "",
		},
		searchMultipleToolTipText: {
			type: String,
			default: "",
		},
		searchMultipleToolTipPreText: {
			type: String,
			default: "",
		},
		searchMultipleType: {
			type: String,
			default: "",
		},
		searchMultipleOrderByList: {
			type: Array,
			default: () => {
				return [];
			},
		},
		searchMultipleOrderByMax: {
			type: Number,
			default: 0,
		},
		searchMultipleOrderByDefault: {
			type: String,
			default: null,
		},
		searchMultipleOrderByDefaultDirection: {
			type: String,
			default: null,
		},
		showCSV: {
			type: Boolean,
			default: false,
		},
		showPDF: {
			type: Boolean,
			default: false,
		},
		baseButtonSettings: Object,
		addNewButtonLabel: {
			type: String,
		},
		forceSearchFieldUpdate: {
			type: Object,
		},
		name: {
			type: String,
			default: "",
		},
		searchMultipleHiddenByDefault: {
			type: [Array, String],
			default: () => {
				return [];
			},
		},
		filterQueryTitle: {
			type: String,
			default: "Showing results for...",
		},
		filterQueryDescriptionOverride: {
			type: String,
			default: "",
		},
		showSaveFilter: {
			type: Boolean,
			default: false,
		},
		meta: {
			type: Object,
			default: () => {
				return {};
			},
		},
		queryKey: {
			type: Number,
			default: 0,
		},
		canSave: {
			type: Boolean,
			default: false,
		},
		hadKeyUpdate: {
			type: Boolean,
			default: false,
		},
		savedSearchType: {
			type: String,
			default: "",
		},
		doLoadMore: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			NEWBITS: true,
			filters: {},
			showTableSearch: false,
			internalPagination: false,
			hasShowMore: false,
			showMore: [],
			internalPage: 1,
			internalPages: 0,
			currentFilterCount: 0,
			internalFiltering: false,
			search: {},
			selectAll: false,
			selected: {},
			checkboxVar: null,
			checkboxColumn: null,
			checkboxFilter: null,
			searched: false,
			hasCheckbox: -1,
			scrollToTopOnChange: false,
			searchMultipleVars: {},
			showHeadings: [],
			showHeadingFilters: {},
			hiddenHeadingInfo: "",
			lastSearch: null,
			searchQuery: [],
			orderBySelected: [],
			initialLoad: false,
			initialLoadDone: false,
			tempHideFilter: false,
			stickyOn: false,
			showSaveSearchModal: false,
			showSavedSearchesModal: false,
			savedSearches: [],
		};
	},
	watch: {
		searchMultipleValuesCompd: {
			handler(val) {
				this.tempHideFilter = false;

				if (!!this.searchMultipleValuesCompd.query && !!this.searchMultipleValuesCompd.query.length && !this.initialLoadDone) {
					this.initialLoad = true;
				}
			},
			deep: true,
		},
		showHeadings: {
			handler(val) {
				if (this.name) {
					this.tableHeading({
						tableName: this.tableName,
						sortingInfo: general.stripObject(this.showHeadings),
					});
				}
			},
			deep: true,
		},
		tableHeadingVisibility: {
			handler(val) {
				if (this.name) {
					let diff = this.tableHeadingVisibility[this.name].filter((d) => !this.showHeadings.includes(d));
					if (diff.length > 0 && !this.hideHeadingShowHide) {
						Vue.set(this, "showHeadings", this.tableHeadingVisibility[this.name]);
					}
				}
			},
			deep: true,
		},
		forceSearchFieldUpdate: {
			handler(val) {
				if (Object.keys(val).length > 0) {
					Object.keys(val).forEach(
						function (k) {
							Vue.set(this.search, k, val[k]);
						}.bind(this)
					);

					this.$emit("forceSearchFieldUpdated", true);

					if (val.search) this.searchTable();
				}
			},
			deep: true,
		},
		filters: {
			handler(val) {
				this.thePage = 1;

				if (this.internalPagination === false) {
					Object.keys(this.filters).forEach(
						function (f) {
							if (this.filters[f]) this.search[f] = this.filters[f];
						}.bind(this)
					);

					this.searchTable();
				}
			},
			deep: true,
		},
		items: {
			handler(val) {
				this.selected = {};
				this.showMore = [];
				if (this.scrollToTopOnChange) {
					this.scrollToTopOnChange = false;
					document.getElementById("table-" + this._uid).scrollIntoView();
				}
			},
		},
		hasSearches: {
			handler(val) {
				this.$emit("searchChange", this.search);
			},
			deep: true,
		},
		selectAll: {
			handler(val) {
				if (val === false) {
					this.checkboxFilter = null;
				}
				if (this.checkboxVar !== null) {
					this.items.forEach(
						function (item) {
							const k = this.headings[this.checkboxColumn].checkBoxVar;
							Vue.set(this.selected, item[k], val);
						}.bind(this)
					);
				}
			},
		},
		selected: {
			handler(val, val2) {
				let ids = [];

				Object.keys(this.selected).forEach(
					function (id) {
						if (this.selected[id]) {
							ids.push(id);
						}
					}.bind(this)
				);

				this.$emit("selected", ids);

				if (ids.length === 0) {
					this.checkboxFilter = null;
				}
			},
			deep: true,
		},
		checkboxFilter: {
			handler(val) {
				this.$emit("checkboxFilter", this.checkboxFilter);
			},
		},
		triggerSomething: {
			handler(val) {
				switch (val) {
					case "multipleSearchModal":
						this.initialLoadDone = true;
						this.initialLoad = false;
						this.doSearchMultiple();

						this.$emit("trigger-complete", val);

						break;

					case "closeSearchModal":
						this.showTableSearch = false;

						break;
				}
			},
		},
	},
	computed: {
		...mapState("general", ["tableSortingInfo", "tableHeadingVisibility"]),

		showSave() {
			return !!this.getSavedSearchType && this.canSave && this.hasSearches;
		},

		getSavedSearchType() {
			return this.savedSearchType || this.name || "";
		},

		showSimpleSearch() {
			if (!this.searchMultiple) return true;

			return Object.keys(this.searchSetupMultiple).length > 0 && Object.keys(this.searchSetup).length > 0;
		},

		searchSetupMultipleProcessed() {
			if (Object.keys(this.searchSetupMultiple).length === 0) return this.searchSetup;

			return this.searchSetupMultiple;
		},

		hideThePagination() {
			return this.hidePagination || (this.loading && !this.doLoadMore);
		},

		searchMultipleTitleCompd() {
			return this.searchMultipleTitle;
		},

		searchMultipleDescriptionCompd() {
			return this.searchMultipleDescription;
		},

		searchMultipleValuesCompd() {
			if (this.searchMultipleLastUpdate < 0) return;

			if (Array.isArray(this.searchMultipleValues)) {
				return {
					query: this.searchMultipleValues,
				};
			}

			return this.searchMultipleValues;
		},

		toggleHeadingsButtonLabel() {
			let totalHeadings = 0,
				showCount = 0,
				label = "Show/hide table columns",
				n = this.name || "default",
				hiddenLabels = [];

			this.processedHeadings.forEach(
				function (h) {
					if (
						(typeof h.checkBox !== "undefined" && h.checkBox) ||
						(!this.headings[h.var].valueForFilter && !this.headings[h.var].value) ||
						this.headings[h.var].alwaysVisible
					)
						return;

					if (this.showHeadings.length > 0) {
						if (this.showHeadings.indexOf(h.var) >= 0) ++showCount;
						else {
							hiddenLabels.push("<strong>" + (this.headings[h.var].valueForFilter || this.headings[h.var].value) + "</strong>");
						}

						return;
					}

					++showCount;
				}.bind(this)
			);

			if (hiddenLabels.length > 0) {
				let hiddenHeadingInfo;

				if (hiddenLabels.length === 1) hiddenHeadingInfo = "The " + hiddenLabels[0] + " column is currently hidden";
				else {
					const finalName = hiddenLabels.pop();

					hiddenHeadingInfo = "Hidden columns: " + (hiddenLabels.length ? hiddenLabels.join(", ") + " & " + finalName : finalName);
				}

				this.hiddenHeadingInfo = hiddenHeadingInfo;
			} else {
				this.hiddenHeadingInfo = "";
			}

			this.headingsToShow.forEach(function (h) {
				totalHeadings += h.headings.length;
			});

			return label;
		},
		headingClasses2() {
			let rtn = {};

			this.processedHeadings.forEach(
				function (h) {
					let classes = {
						hide: !!h.hide,
					};
					
					if (h.alignHeading) {
						classes[h.alignHeading] = true;
					}

					if (h.checkBox) {
						classes.checkBox = true;
					}

					if (typeof h.sort !== "undefined" && h.sort !== false) {
						let ignore =
							this.searchMultipleValuesCompd &&
							this.searchMultipleValuesCompd.orderBy &&
							this.searchMultipleValuesCompd.orderBy.length > 0;
						ignore = false;
						if (!ignore) {
							classes.sort = true;

							if (typeof this.sorted[h.var] !== "undefined") {
								classes.sorted = true;

								if (this.sorted[h.var] === -1) {
									classes.desc = true;
								}
							}
						}
					}

					rtn[h.var] = [classes];
				}.bind(this)
			);

			return rtn;
		},
		showHeadingFiltersInternal() {
			let n = this.name || "default";

			return !!this.showHeadingFilters[n];
		},
		headingsToShow() {
			let headings = {};

			const hasCheckbox = this.processedHeadings.find((h) => typeof h.checkBox !== "undefined" && h.checkBox);

			let showingCount = this.showHeadings.length;

			if (hasCheckbox) --showingCount;

			Object.keys(this.headings).forEach(
				function (heading) {
					if (this.headings[heading].checkBox || this.headings[heading].alwaysVisible || this.hideHeadingShowHide ) return;

					if (!this.headings[heading].valueForFilter && !this.headings[heading].value) return;

					let typeLabel = this.headings[heading].type || "default";

					headings[typeLabel] = headings[typeLabel] || [];

					headings[typeLabel].push({
						_id: heading,
						label: this.headings[heading].valueForFilter ? this.headings[heading].valueForFilter : this.headings[heading].value,
						disabled: showingCount === 1 && this.showHeadings.indexOf(heading) !== -1,
					});
				}.bind(this)
			);

			let rtn = [];

			Object.keys(headings).forEach(function (type) {
				let hh = general.stripObject(headings[type]);

				rtn.push({ type: type, headings: headings[type] });
			});

			return rtn;
		},
		searchMultipleFilterCount() {
			let count = 0;

			if (!this.searchMultipleValuesCompd.query) return count;

			this.searchMultipleValuesCompd.query.forEach(
				function (searchLine) {
					Object.keys(searchLine).forEach(
						function (searchItem) {
							let ss = searchItem.split("____")[0];

							if (!this.searchSetupMultipleProcessed[ss]) return;

							if (typeof searchLine[searchItem] !== "object") {
								!!searchLine[searchItem] && ++count;
							} else if (searchLine[searchItem] !== null) {
								Object.keys(searchLine[searchItem]).forEach(function (a) {
									let b = searchLine[searchItem][a];

									if (["value", "from", "to"].indexOf(a) < 0) return;

									if (!!b || b === 0) ++count;
								});
							}
						}.bind(this)
					);
				}.bind(this)
			);

			return count;
		},
		searchMultipleButtonLabelInternal() {
			return this.searchMultipleFilterCount > 0
				? "Add/edit filter options (<strong>" + this.searchMultipleFilterCount + " currently</strong>)"
				: this.searchMultipleButtonLabel;
		},
		colspan() {
			return this.processedHeadings.length + 2;
		},
		hasSearches() {
			let count = 0;

			Object.keys(this.search).forEach(
				function (f) {
					//if ( f === 'frequency') return;
					if (Array.isArray(this.search[f]) && this.search[f].length === 0) return;

					if (this.search[f] === "" || this.search[f] === null) return;

					++count;
				}.bind(this)
			);

			return count;
		},
		hasOptionsSelected() {
			let r = false;

			Object.keys(this.selected).forEach(
				function (item) {
					if (this.selected[item]) r = true;
				}.bind(this)
			);

			return r;
		},
		checkboxOptions() {
			if (this.checkboxColumn !== null && typeof this.headings[this.checkboxColumn].options !== "undefined") {
				return this.headings[this.checkboxColumn].options;
			}

			return [];
		},
		emptyLabelProcessed() {
			if (this.loading) return this.loadingText;

			if (this.searched) return this.emptySearch;

			return this.emptyLabel;
		},
		thePages: {
			get: function () {
				return this.internalPagination ? Math.ceil(this.currentFilterCount / this.more) : this.pages;
			},
			set: function (newValue) {
				if (this.internalPagination) this.internalPages = newValue;
			},
		},
		thePage: {
			get: function () {
				return this.internalPagination ? this.internalPage : parseInt(this.page);
			},
			set: function (newValue) {
				if (this.internalPagination) this.internalPage = newValue;
			},
		},
		searchInputs() {
			let searchSetup = [];

			Object.keys(this.searchSetup).forEach(
				function (k) {
					let item = this.searchSetup[k];

					if (item.hide) return;

					item.var = k;

					searchSetup.push(item);

					if (typeof item.values !== "undefined") {
						Vue.set(this.search, k, item.values);
					}
				}.bind(this)
			);

			return searchSetup;
		},
		classesProcessed() {
			let c = {
				"table-wrap": true,
				"has-mobile": true,
				"showing-filters": this.showHeadingFiltersInternal,
			};

			this.classes.forEach(function (cc) {
				c[cc] = true;
			});

			return c;
		},
		processedHeadings() {
			let headings = [];

			if (Array.isArray(this.headings)) {
				this.headings.forEach(
					function (heading) {
						let h = {
							value: heading,
							var: heading,
							hide: this.name ? ( this.showHeadings.length > 0 && this.showHeadings.indexOf(heading) < 0 ) : false,
						};

						if (heading.headingComponent) {
							h.headingComponent = heading.headingComponent;

							h.settings = heading.headingComponentSettings || {};

							if (!h.settings.icon) h.settings.icon = heading;

							if (!h.settings.label) h.settings.label = heading.value;
						}

						headings.push(h);
					}.bind(this)
				);
			} else {
				Object.keys(this.headings).forEach(
					function (heading, index) {
						
						let h = general.stripObject( this.headings[heading] );

						h.hide = !this.hideHeadingShowHide && this.showHeadings.length > 0 && this.showHeadings.indexOf(heading) < 0 && !h.alwaysVisible;

						h.var = heading;

						if (h.checkBox) this.hasCheckbox = index;

						if (h.headingComponent) {
							h.settings = h.headingComponentSettings || {};

							if (!h.settings.icon) h.settings.icon = heading;

							if (!h.settings.label) h.settings.label = h.value;
						}

						headings.push(h);
					}.bind(this)
				);
			}

			return headings;
		},
		processedItems() {
			if (this.loading && !this.doLoadMore) return [];

			if (Array.isArray(this.headings)) {
				return this.items;
			}

			let items = [],
				currentCount = 0;

			this.resetCurrentFilterCount();

			this.items.forEach(
				function (item, itemIndex) {
					let newItem = [],
						ok = true,
						cellHadColspan = {},
						iii = -1;

					Object.keys(this.headings).forEach(
						function (headingVar) {
							let cell = {
								value:
									typeof this.headings[headingVar].emptyCell !== "undefined" ? this.headings[headingVar].emptyCell : this.emptyCell,
								type: "normal",
								headingVar: headingVar,
								checkBox: false,
								colspan: 1,
								highlight: !!item.highlight,
								hideHeadingMobile: this.headings[headingVar].value === "" || !this.headings[headingVar].value,
								hideHeadingDesktop: this.headings[headingVar].value === "" || !this.headings[headingVar].value,
								mobileClass: {},
								mobileShowMoreOnly: !!this.headings[headingVar].mobileShowMoreOnly,
							};

							if (cell.mobileShowMoreOnly) this.hasShowMore = true;

							if (this.headings[headingVar].mobileClass) {
								this.headings[headingVar].mobileClass.forEach(function (c) {
									cell.mobileClass[c] = true;
								});
							}

							++iii;

							if (Object.keys(cellHadColspan).length > 0) {
								if (this.validCell(item, headingVar)) {
									let diff = iii - cellHadColspan.index;

									if (newItem[cellHadColspan.index].colspan > diff) newItem[cellHadColspan.index].colspan = diff;

									cellHadColspan = {};
								} else {
									--iii;

									return;
								}
							}

							let colspan = 1;

							if (!!item.colspan && item.colspan > 1) {
								colspan = item.colSpan;
							} else if (!!this.headings[headingVar].colspan && this.headings[headingVar].colspan > 1) {
								colspan = this.headings[headingVar].colspan;
							}

							if (colspan > 1 && this.validCell(item, headingVar)) {
								cellHadColspan = {
									index: iii,
									colspan: colspan,
								};
								cell.colspan = colspan;
							}

							cell.align = this.headings[headingVar].alignCell ? this.headings[headingVar].alignCell : null;

							if (
								typeof this.headings[headingVar].checkBox !== "undefined" &&
								typeof item[this.headings[headingVar].checkBoxVar] !== "undefined"
							) {
								let checkBoxVar = item[this.headings[headingVar].checkBoxVar];

								cell.checkBox = typeof item[headingVar] === "undefined" || item[headingVar] === true ? true : false;

								cell.checkBoxVar = checkBoxVar;

								this.checkboxVar = checkBoxVar;

								this.checkboxColumn = headingVar;
							} else if (typeof item[headingVar] !== "undefined" && item[headingVar] !== null) {
								if (typeof item[headingVar] === "object") {
									// { value: '', href: '', target: '' }

									// if cell.value is an array then we need to setup some discs

									let filter = { by: null, value: null };

									if (
										typeof this.headings[headingVar].filterBy !== "undefined" &&
										typeof this.filters[headingVar] !== "undefined" &&
										this.filters[headingVar] !== ""
									) {
										filter.by = this.headings[headingVar].filterBy;
										filter.value = this.filters[headingVar];
									}

									if (typeof item[headingVar].value === "object" && Array.isArray(item[headingVar].value) && ok) {
										cell.type = "discs";

										cell.discs = [];

										// if it also has a click value then this is for the discs edit overlay, it should also have a label

										if (filter.by === "colour" && this.internalFiltering) ok = false;

										item[headingVar].value.forEach(function (disc) {
											let d = typeof disc === "string" ? { value: disc } : disc;

											d.colour = d.colour ? d.colour : "white";

											if (filter.by === "colour" && filter.value === d.colour) ok = true;

											d.class = {};

											if (d.colour.substr(0, 1) === "#") {
												d.style = "background-color:" + d.colour + ";";
											} else {
												d.class[d.colour] = true;
											}

											cell.discs.push(d);
										});
									} else {
										if (
											this.headings[headingVar].hideHeadingMobile ||
											(typeof item[headingVar].value === "object" && item[headingVar].value.hideHeadingMobile)
										) {
											cell.hideHeadingMobile = true;
										}

										if (
											this.headings[headingVar].hideHeadingDesktop ||
											(typeof item[headingVar].value === "object" && item[headingVar].value.hideHeadingDesktop)
										) {
											cell.hideHeadingDesktop = true;
										}

										if (
											this.headings[headingVar].hideHeading ||
											(typeof item[headingVar].value === "object" && item[headingVar].value.hideHeading)
										) {
											cell.hideHeadingMobile = true;
											cell.hideHeadingDesktop = true;
										}

										if (typeof this.headings[headingVar].component !== "undefined") {
											cell.type = "component";
											cell.component = this.headings[headingVar].component;
											cell.value = item[headingVar];
											cell.var = headingVar;
										} else {
											cell.value = item[headingVar];

											if (typeof this.headings[headingVar].dateFormat !== "undefined") {
												cell.value = general.dateFormat(this.headings[headingVar].dateFormat, cell.value);
											}

											if (
												filter.by &&
												typeof cell[filter.by] !== "undefined" &&
												cell[filter.by] !== filter.value &&
												this.internalFiltering
											) {
												ok = false;
											}
										}
									}

									cell = { ...cell, ...item[headingVar] };
								} else {
									cell.value = item[headingVar];

									if (typeof this.headings[headingVar].dateFormat !== "undefined") {
										cell.value = general.dateFormat(this.headings[headingVar].dateFormat, cell.value);
									}

									cell.selectCheckbox = false;

									if (typeof this.headings[headingVar].selectCheckbox !== "undefined" && this.headings[headingVar].selectCheckbox) {
										cell.selectCheckbox = true;
									}

									if (typeof this.headings[headingVar].click !== "undefined") {
										cell.click = this.headings[headingVar].click;

										cell.clickVars = {};

										this.headings[headingVar].clickVars.forEach(
											function (clickVar) {
												if (typeof item[clickVar] !== "undefined") {
													cell.clickVars[clickVar] = item[clickVar];
												}
											}.bind(this)
										);
									}
								}
							}

							newItem.push(cell);
						}.bind(this)
					);

					if (ok) {
						if (this.internalPagination) {
							let start = this.thePage > 1 ? (this.thePage - 1) * this.more - 1 : 0;

							if (currentCount < start || currentCount > start + this.more - 1) ok = false;
						}

						++this.currentFilterCount;

						newItem.index = itemIndex;

						if (ok) items.push(newItem);

						++currentCount;
					}
				}.bind(this)
			);

			return items;
		},
		tableName() {
			return this.name || "default";
		},
		hasOrderBy() {
			return this.searchMultipleOrderByList.length > 0;
		},
		searchObject() {
			let s = general.stripObject(this.search);

			if (this.searchMultiple && this.showSimpleSearch) {
				// has both search types and this was a simple one.

				s = {};

				if (this.NEWBITS) {
					s = {
						query: this.searchMultipleValues.query ? general.stripObject(this.searchMultipleValues.query) : [],
						orderBy: this.searchMultipleValues.orderBy ? general.stripObject(this.searchMultipleValues.orderBy) : [],
					};
				}

				Object.keys(this.search).forEach(
					function (k) {
						if (["query", "filterWasChanged", "orderBy"].indexOf(k) >= 0) return;

						if (this.searchSetup[k]) {
							let q = {};

							switch (this.searchSetup[k].type) {
								case "text":
									q = {
										value: this.search[k],
										comparison: "like",
									};

									break;
							}

							if (Object.keys(q).length) {
								s.query = s.query || [];
								s.query[0] = s.query[0] || {};
								s.query[0][k] = q;
							}
						}
					}.bind(this)
				);
			}

			return s;
		},
	},
	methods: {
		...mapMutations("general", ["tableSorting", "tableHeading"]),

		loadMore() {
			this.$emit("loadMore");
		},

		showSavedSearches() {
			this.showSavedSearchesModal = true;
		},

		loadSavedSearches() {
			if (!this.getSavedSearchType) return;
			ApiService.get(`saved-searches/${this.getSavedSearchType}`)
				.then((response) => {
					this.savedSearches = response.data;
				})
				.catch(() => {});
		},

		runSavedSearch(search) {
			let doIt = false;
			// Apply any saved sorting
			if (search.sort) {
				let sort = { ...search.sort, init: true };
				this.$emit("sort", sort);
				doIt = true;
			}

			// Apply the saved search filters
			if (search.filters) {
				Vue.set(this, "search", search.filters);
				doIt = true;
			}

			if (doIt)
				setTimeout(
					function () {
						this.searchTable();
					}.bind(this),
					100
				);

			this.showSavedSearchesModal = false;
		},

		deleteSavedSearch(searchId) {
			ApiService.delete(`saved-searches/${this.getSavedSearchType}/${searchId}`)
				.then(() => {
					this.$snotify.success("Search deleted successfully");
					this.savedSearches = this.savedSearches.filter((s) => s._id !== searchId);
				})
				.catch(() => {
					this.$snotify.error("Failed to delete search");
				});
		},

		saveSearch() {
			this.showSaveSearchModal = true;
		},

		handleSaveSearch(name) {
			const search = {
				name,
				filters: this.searchObject,
				sort: this.sorted,
			};

			ApiService.post("saved-searches/" + this.getSavedSearchType, search)
				.then(() => {
					this.$snotify.success("Search saved successfully");
					this.showSaveSearchModal = false;
				})
				.catch(() => {
					this.$snotify.error("Failed to save search");
				});
		},

		saveFilter(info) {
			this.$emit("saveFilter", info);
		},

		filtersChanged(info) {
			this.$emit("filtersChanged", info);
		},

		toggleHeadingFilters() {
			Vue.set(this.showHeadingFilters, this.tableName, !this.showHeadingFilters[this.tableName]);

			TokenService.saveShowTableFilters(this.showHeadingFilters);
		},

		closeTableFilter(info) {
			general.modalClose();
			this.showTableSearch = false;
			this.meta.filterInfo = this.meta.filterInfo || {};
			this.meta.filterInfo.instantClose = false;

			if (info.runSearch) {
				if (typeof info.searchQuery !== "undefined") Vue.set(this, "searchQuery", info.searchQuery);
				if (typeof info.orderBySelected !== "undefined") Vue.set(this, "orderBySelected", info.orderBySelected);
			}

			if (this.initialLoad) {
				this.initialLoad = false;
				return;
			}

			this.$emit("closeSearchMultiple", info.runSearch ? info.processed : null);
		},
		clearFromFilter() {
			this.tempHideFilter = true;
			if (this.showSimpleSearch) {
				this.search = {};
				this.showMore = [];
				this.lastSearch = null;
			}
			this.closeTableFilter({
				searchQuery: [],
				orderBySelected: [],
				processed: [],
				runSearch: true,
				filterWasChanged: true,
			});
		},
		doSearchMultiple() {
			this.initialLoadDone = true;
			this.showTableSearch = true;
		},
		doShowMore(i, hide) {
			if (hide) {
				let p = this.showMore.indexOf(i);
				this.showMore.splice(p, 1);
			} else this.showMore.push(i);
		},
		showingMore(i) {
			return !this.hasShowMore ? true : this.showMore.indexOf(i) >= 0;
		},
		resetCurrentFilterCount() {
			this.currentFilterCount = 0;
		},
		validCell(item, headingVar) {
			return typeof item[headingVar] !== "undefined" && item[headingVar] !== "" && item[headingVar] !== null && item[headingVar] !== false;
		},
		changeDate(searchInputVar, info) {
			this.search[searchInputVar] = info;
		},
		clicked(ev, theVar, value, n) {
			let eventName = ev && ev.eventOverride ? ev.eventOverride : this.headings[theVar].event || null;

			if (eventName) {
				if (n) value.n = n;

				if (ev && ev.returnEvent) value = ev;

				this.$emit(eventName, value);
			}
		},
		doDownload(type) {
			this.$emit(type);
		},
		selectedSelect(theVar, value, filterIndex) {
			if (typeof this.search[theVar] !== "undefined" && this.search[theVar] !== "") {
				if (value === this.search[theVar]) return "selected";
			} else if (filterIndex === 0) {
				return "selected";
			}

			return "selected";
		},
		clearSearch() {
			this.search = {};
			this.showMore = [];
			this.$emit("clear");
			this.searchTable();
			this.lastSearch = null;
		},
		searchTable() {
			this.searched = Object.keys(this.searchObject).length > 0;

			this.lastSearch = new Date();

			if (this.searchMultiple && this.NEWBITS) {
				this.meta.filterInfo = this.meta.filterInfo || {};

				Vue.set(this, "searchQuery", this.searchObject.query);
				Vue.set(this, "orderBySelected", this.searchObject.orderBy);

				this.meta.filterInfo.instantClose = true;

				this.showTableSearch = true;
			}

			this.$emit("search", this.searchObject);
		},
		clickHeader(heading) {
			if (!Array.isArray(this.headings)) {
				if (typeof heading.sort !== "undefined" && heading.sort !== false) {
					let s = heading.sort;

					if (typeof this.sorted[heading.var] !== "undefined") {
						s = this.sorted[heading.var] === -1 ? 1 : -1;
					}

					let sorted = {};

					sorted[heading.var] = s;

					if (this.name && !this.searchMultiple) {
						this.tableSorting({
							tableName: this.tableName,
							sortingInfo: sorted,
						});
					}

					if (this.searchMultiple) {
						let info = {
							processed: general.stripObject(this.searchMultipleValuesCompd),
							runSearch: true,
						};

						info.processed.orderBy = [{ field: heading.var, direction: s }];

						info.orderBySelected = info.processed.orderBy;

						info.processed.filterWasChanged = true;

						this.closeTableFilter(info);
					} else {
						this.$emit("sort", sorted);
					}
				} else if (heading.headingEvent) {
					this.$emit(heading.headingEvent, heading);
				}
			}
		},
		baseButtonClick() {
			this.$emit("base-button-click", this.baseButtonSettings.click);
		},
		changePage(pageNumber) {
			switch (pageNumber) {
				case "start":
					pageNumber = 1;
					break;
				case "previous":
					pageNumber = this.thePage - 1;
					break;
				case "next":
					pageNumber = this.thePage + 1;
					break;
				case "end":
					pageNumber = this.thePages;
					break;
			}

			if (this.internalPagination) {
				this.thePage = pageNumber;
			} else {
				this.scrollToTopOnChange = true;
				document.getElementById("table-" + this._uid).scrollIntoView();
				this.$emit("pagination", { page: pageNumber });
			}
		},
		headingClasses(headingVar) {
			let h = this.processedHeadings[headingVar],
				classes = {
					hide: !!h.hide,
				};

			if (h.alignHeading) {
				classes[h.alignHeading] = true;
			}

			if (h.checkBox) {
				classes.checkBox = true;
			}

			if (typeof h.sort !== "undefined" && h.sort !== false) {
				classes.sort = true;

				if (typeof this.sorted[h.var] !== "undefined") {
					classes.sorted = true;

					if (this.sorted[h.var] === -1) {
						classes.desc = true;
					}
				}
			}

			return [classes];
		},
		headingStyles(headingVar) {
			if (!Array.isArray(this.headings)) {
				let n = Object.keys(this.headings)[headingVar],
					h = this.headings[n];

				return h.styles ? h.styles : "";
			}

			return "";
		},
		trClasses(item) {
			let classes = {
				highlighted: !!item[0].highlight,
			};
			return [classes];
		},
		cellClasses(cell) {
			// Find the processed heading to get the correct hide status
			const processedHeading = this.processedHeadings.find(h => h.var === cell.headingVar);
			
			let classes = {
				selected: false,
				disabled: false,
				hide: processedHeading ? !!processedHeading.hide : !!this.headings[cell.headingVar].hide,
			};

			if (cell.align) {
				classes[cell.align] = true;
			}

			classes.checkbox = !!cell.checkBox;

			if (cell.type === "discs" && typeof cell.click !== "undefined" && typeof cell.label !== "undefined") {
				classes.editable = true;
			}

			return [classes];
		},
		clickCell(cellInfo) {
			if (cellInfo.selectCheckbox && typeof cellInfo.clickVars._id !== "undefined") {
				let t = {};

				t[cellInfo.clickVars._id] = true;

				Vue.set(this, "selected", t);
			}

			if (typeof cellInfo.click !== "undefined") this.$emit(cellInfo.click, cellInfo.clickVars, cellInfo.headingVar);
		},
		addNew() {
			this.$emit("addNew");
		},
		headingBitsOnLoad() {
			if (this.name) {
				if (this.tableSortingInfo[this.name]) {
					let sorted = general.stripObject(this.tableSortingInfo[this.name]);

					sorted.init = true;

					this.$emit("sort", sorted);
				}

				if (
					!this.hideHeadingShowHide && 
					this.tableHeadingVisibility[this.name] &&
					Array.isArray(this.tableHeadingVisibility[this.name]) &&
					this.tableHeadingVisibility[this.name].length > 0
				) {
					Vue.set(this, "showHeadings", general.stripObject(this.tableHeadingVisibility[this.name]));
				}
			}

			if (!this.showHeadings.length) {
				Object.keys(this.headings).forEach(
					function (heading) {
						if (this.headings[heading].hide) {
							return;
						}

						this.showHeadings.push(heading);
					}.bind(this),
				);
			}

			// Ensure alwaysVisible headings are always in showHeadings
			Object.keys(this.headings).forEach(
				function (heading) {
					if (this.headings[heading].alwaysVisible && this.showHeadings.indexOf(heading) < 0) {
						this.showHeadings.push(heading);
					}
				}.bind(this),
			);

			Vue.set(this, "showHeadingFilters", TokenService.getShowTableFilters());
		},
	},
	created() {
		if (this.hadKeyUpdate) return;

		this.internalFiltering = this.hasInternalFiltering;

		if (this.thePages === 0) {
			// Internal pagination

			this.internalFiltering = true;

			if (this.more > 0 && this.items.length > this.more) {
				this.internalPagination = true;
			}
		}

		this.$emit("ready", true);
	},
	mounted() {
		this.$emit("tableMounted");
	},
	beforeMount() {
		if (this.hadKeyUpdate) return;

		this.headingBitsOnLoad();

		this.loadSavedSearches();
	},
};
/**
* HEADINGS

name: { 
  value: 'Project', HEADING TEXT
  click: 'selectProject', FUNCTION FOR WHEN CELL IS CLICKED, USED AS EMIT ON TABLE
  clickVars: ['_id'], VARS THAT ARE PASSED TO FUNCTION ABOVE, VARIABLE NAME OR ARRAY OF THEM
  alignCell: 'left', 
  hide: true,
  alwaysVisible: true, FORCES COLUMN TO ALWAYS BE VISIBLE AND EXCLUDES FROM SHOW/HIDE INTERFACE
  emptyCell: '',
  sort: -1, MAKES COLUMN SORTABLE
  filterBy: 'colour', FILTERS ON COLOURED DISCS, MUST HAVE more PROP SET TO ENABLE INTERNAL PAGINATION
  filters:[ 
    { label: 'All', all: true }, 
    { label: 'Poor', value: 'red' }, 
    { label: 'Review', value: 'orange' }, 
    { label: 'Good', value: 'green' } 
  ]
}


*/
</script>
<style lang="less" scoped>
@import "./../assets/css/less/_helper.less";
@import "./../assets/css/less/_settings.less";
@imgRoot: "../assets/";
.the-query {
	margin: 10px 10px 20px !important;
	&.hide-query {
		display: none;
	}
}
.sticky-btn {
	position: absolute;
	top: -40px;
	right: 20px;
	z-index: 1;
	display: none;

	span {
		font-size: 0.8em;
		cursor: pointer;
		&:hover {
			text-decoration: underline;
		}
	}

	svg {
		cursor: pointer;
	}

	&.active {
		position: fixed;
		top: 5px;
		right: 20px;
		z-index: 99999999;
		background-color: @darkContrast;
		.trans;
		width: 20px;
		height: 20px;
		.rounded(50%);
		svg {
			width: 50%;
			display: block;
			position: absolute;
			left: 25%;
			top: 10%;
			fill: #fff;
			color: #fff;
		}

		span {
			display: none;
		}

		&:hover {
			background-color: #fff;

			svg {
				fill: @darkContrast;
				color: @darkContrast;
			}
		}
	}
}
.show-headings {
	background-color: @darkContrast;
	border-radius: 5px;
	margin-bottom: 20px;
	padding: 10px;
	margin: 0 10px 20px;
	text-align: center;
	padding-bottom: 6px;

	.show-headings-heading {
		font-size: 16px;
		margin-bottom: 5px;
		color: #fff;
	}

	.show-headings-wrap {
		display: inline-block;
		margin: 0 30px 0 0;
		display: none;

		.show-headings-sub-heading {
			font-size: 14px;
			margin-bottom: 5px;
		}
	}

	&:not(.show-filters) {
		.trans;
		margin-top: 10px;

		&:hover {
			background-color: @formDefaultColour;
		}
	}

	&.show-filters {
		padding: 10px;
		width: auto;
		left: auto;
		border-radius: 5px 5px 0 0;
		margin: 0px 10px;
		display: block;

		.show-headings-wrap {
			display: block;
		}

		.show-headings-heading {
			margin-bottom: 5px;
			font-size: 16px;
			text-align: left;

			&:hover {
				&:after {
					content: "hide section";
					display: inline-block;
					padding-left: 15px;
					font-size: 0.7em;
				}
			}
		}
	}
}
.show-headings-wrap {
	.show-headings-heading-breakdown {
		margin: 0 0 10px 20px;
		text-align: left;
		font-size: 14px;
		display: inline-block;
		span {
			font-weight: 500;
		}
	}
}
.table-wrap {
	background-color: #fff;
	padding: 5px 0 0;
	max-width: 100%;
	overflow: visible;
	overflow: hidden;

	&:not(.no-corners) {
		border-radius: 8px;
		.panelShadow;
	}

	[type="checkbox"],
	[type="radio"] {
		&:not(old) {
			position: absolute; // NEW: take out of normal flow
			width: 18px; // NEW: match custom checkbox size
			height: 18px; // NEW: match custom checkbox size
			margin: 0; // Remove default margin
			padding: 0; // Remove default padding
			opacity: 0;
			z-index: 2; // Ensure it's above the label for clickability
			cursor: pointer;

			& + label {
				display: inline-block;
				margin-left: 0; // Remove negative margin
				padding-left: 25px;
				line-height: 18px; // Match the checkbox size for vertical alignment
				position: relative;
				vertical-align: top; // <-- change this
				&:before {
					width: 18px;
					height: 18px;
					content: "";
					position: absolute;
					left: 0;
					top: 1px;
					background: url("data:image/svg+xml;utf8,<svg width='138' height='64' fill='none' xmlns='http://www.w3.org/2000/svg'><mask id='a' maskUnits='userSpaceOnUse' x='1' y='48' width='16' height='16'><rect x='1' y='48' width='16' height='16' rx='4' fill='%23C4C4C4'/></mask><g mask='url(%23a)'><path fill='@{formDefaultColourNoHash}' d='M1 48h16v16H1z'/></g><g filter='url(%23filter0_d)'><path fill-rule='evenodd' clip-rule='evenodd' d='M13.792 52.236a.758.758 0 01-.028 1.07l-6.06 5.758a.758.758 0 01-1.058-.013l-2.424-2.424a.758.758 0 011.071-1.072l1.902 1.902 5.526-5.249a.758.758 0 011.07.028z' fill='%23fff'/></g><path d='M133.381 4.299a.75.75 0 00-1.265.807l1.265-.807zm-3.479-1.41a.75.75 0 00.809-1.264l-.809 1.264zM133.25 9a7.25 7.25 0 01-7.25 7.25v1.5A8.75 8.75 0 00134.75 9h-1.5zM126 16.25A7.25 7.25 0 01118.75 9h-1.5a8.75 8.75 0 008.75 8.75v-1.5zM118.75 9A7.25 7.25 0 01126 1.75V.25A8.75 8.75 0 00117.25 9h1.5zm13.366-3.894A7.21 7.21 0 01133.25 9h1.5a8.71 8.71 0 00-1.369-4.701l-1.265.807zM126 1.75c1.438 0 2.776.418 3.902 1.139l.809-1.264A8.712 8.712 0 00126 .25v1.5z' fill='%23EAEDF0'/><g filter='url(%23filter1_d)'><path fill-rule='evenodd' clip-rule='evenodd' d='M135.027 2.429A.757.757 0 01135 3.5l-9.296 8.565a.758.758 0 01-1.058-.014l-2.424-2.424a.757.757 0 111.071-1.072l1.902 1.902 8.761-8.055a.758.758 0 011.071.027z' fill='%23B3C0CE'/></g><circle cx='68' cy='9' r='8' stroke='%23EAEDF0' stroke-width='1.5'/><path d='M16.38 4.299a.75.75 0 10-1.263.807l1.264-.807zm-3.478-1.41a.75.75 0 10.809-1.264l-.809 1.264zM16.25 9A7.25 7.25 0 019 16.25v1.5A8.75 8.75 0 0017.75 9h-1.5zM9 16.25A7.25 7.25 0 011.75 9H.25A8.75 8.75 0 009 17.75v-1.5zM1.75 9A7.25 7.25 0 019 1.75V.25A8.75 8.75 0 00.25 9h1.5zm13.367-3.894A7.212 7.212 0 0116.25 9h1.5a8.712 8.712 0 00-1.37-4.701l-1.263.807zM9 1.75c1.438 0 2.776.418 3.902 1.139l.809-1.264A8.713 8.713 0 009 .25v1.5z' fill='@{formDefaultColourNoHash}'/><g filter='url(%23filter2_d)'><path fill-rule='evenodd' clip-rule='evenodd' d='M18.027 2.429A.758.758 0 0118 3.5l-9.296 8.565a.758.758 0 01-1.058-.014L5.222 9.627a.758.758 0 011.071-1.072l1.902 1.902 8.761-8.055a.758.758 0 011.072.027z' fill='@{formDefaultColourNoHash}'/></g><mask id='b' maskUnits='userSpaceOnUse' x='57' y='48' width='16' height='16'><rect x='57' y='48' width='16' height='16' rx='4' fill='%23C4C4C4'/></mask><g mask='url(%23b)'><path fill='%23F8FAFB' d='M57 48h16v16H57z'/></g><mask id='c' maskUnits='userSpaceOnUse' x='57' y='48' width='16' height='16'><rect x='57.5' y='48.5' width='15' height='15' rx='3.5' stroke='%23F8FAFB'/></mask><g mask='url(%23c)'><path fill='%23EAEDF0' d='M57 48h16v16H57z'/></g><mask id='d' maskUnits='userSpaceOnUse' x='113' y='48' width='16' height='16'><rect x='113' y='48' width='16' height='16' rx='4' fill='%23C4C4C4'/></mask><g mask='url(%23d)'><path fill='%23F8FAFB' d='M113 48h16v16h-16z'/></g><mask id='e' maskUnits='userSpaceOnUse' x='113' y='48' width='16' height='16'><rect x='113.5' y='48.5' width='15' height='15' rx='3.5' stroke='%23F8FAFB'/></mask><g mask='url(%23e)'><path fill='%23EAEDF0' d='M113 48h16v16h-16z'/></g><g filter='url(%23filter3_d)'><path fill-rule='evenodd' clip-rule='evenodd' d='M125.792 52.236a.757.757 0 01-.028 1.07l-6.06 5.758a.758.758 0 01-1.058-.013l-2.424-2.424a.757.757 0 111.071-1.072l1.902 1.902 5.526-5.249a.758.758 0 011.071.028z' fill='%23B3C0CE'/></g><defs><filter id='filter0_d' x='2' y='51' width='14' height='11.273' filterUnits='userSpaceOnUse' color-interpolation-filters='sRGB'><feFlood flood-opacity='0' result='BackgroundImageFix'/><feColorMatrix in='SourceAlpha' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'/><feOffset dy='1'/><feGaussianBlur stdDeviation='1'/><feColorMatrix values='0 0 0 0 0.266667 0 0 0 0 0.337255 0 0 0 0 0.423529 0 0 0 0.5 0'/><feBlend in2='BackgroundImageFix' result='effect1_dropShadow'/><feBlend in='SourceGraphic' in2='effect1_dropShadow' result='shape'/></filter><filter id='filter1_d' x='120' y='1.193' width='17.236' height='14.08' filterUnits='userSpaceOnUse' color-interpolation-filters='sRGB'><feFlood flood-opacity='0' result='BackgroundImageFix'/><feColorMatrix in='SourceAlpha' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'/><feOffset dy='1'/><feGaussianBlur stdDeviation='1'/><feColorMatrix values='0 0 0 0 0.266667 0 0 0 0 0.337255 0 0 0 0 0.423529 0 0 0 0.25 0'/><feBlend in2='BackgroundImageFix' result='effect1_dropShadow'/><feBlend in='SourceGraphic' in2='effect1_dropShadow' result='shape'/></filter><filter id='filter2_d' x='3' y='1.193' width='17.236' height='14.08' filterUnits='userSpaceOnUse' color-interpolation-filters='sRGB'><feFlood flood-opacity='0' result='BackgroundImageFix'/><feColorMatrix in='SourceAlpha' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'/><feOffset dy='1'/><feGaussianBlur stdDeviation='1'/><feColorMatrix values='0 0 0 0 0.266667 0 0 0 0 0.337255 0 0 0 0 0.423529 0 0 0 0.25 0'/><feBlend in2='BackgroundImageFix' result='effect1_dropShadow'/><feBlend in='SourceGraphic' in2='effect1_dropShadow' result='shape'/></filter><filter id='filter3_d' x='114' y='51' width='14' height='11.273' filterUnits='userSpaceOnUse' color-interpolation-filters='sRGB'><feFlood flood-opacity='0' result='BackgroundImageFix'/><feColorMatrix in='SourceAlpha' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'/><feOffset dy='1'/><feGaussianBlur stdDeviation='1'/><feColorMatrix values='0 0 0 0 0.266667 0 0 0 0 0.337255 0 0 0 0 0.423529 0 0 0 0.25 0'/><feBlend in2='BackgroundImageFix' result='effect1_dropShadow'/><feBlend in='SourceGraphic' in2='effect1_dropShadow' result='shape'/></filter></defs></svg>")
						0 0 no-repeat;
				}
			}
		}
	}

	[type="radio"] {
		&:not(old) {
			& + label {
				&:before {
					background-position: -59px 0;
				}
			}
		}
		&:checked {
			& + label {
				&:before {
					background-position: 0 0;
				}
			}
		}
	}
	[type="checkbox"] {
		&:not(old) {
			& + label {
				&:before {
					background-position: -57px -47px;
				}
			}
		}
		&:checked {
			& + label {
				&:before {
					background-position: 0 -47px;
				}
			}
		}
	}

	&.no-edge {
		border: none;
		padding: 0;
	}

	.new {
		// https://www.flaticon.com/free-icon/plus-button_109433#term=add&page=1&position=23
		background: #fff url("@{imgRoot}img/button-add.svg") 0 4px no-repeat;
		background-size: 20px auto;
		display: inline-block;
		padding: 5px 0 2px 24px;
		cursor: pointer;

		&:hover {
			background-image: url("@{imgRoot}img/button-add-red.svg");
			color: @red;
		}

		&.pulser {
			animation: pulse 2s infinite;
			box-shadow: 0 0 0 rgba(210, 0, 0, 0.4);
			.rounded(10px);
			padding: 5px 10px 5px 30px;
			background-position: 6px 4px;

			:hover {
				animation: none;
			}
		}
	}

	.add-button {
		margin: 20px 0;

		.center {
			text-align: center;
		}

		.large-button {
			margin-top: 0;
			display: inline-block;
		}
	}

	.empty-wrap {
		text-align: center;
		padding: 10px;

		.empty {
			color: @formDefaultColour;
		}
	}
}

.tip {
	margin-left: 10px;
}

.table-title {
	padding: 10px 10px 15px;
	text-align: center;
	margin: 0;
	font-size: 1em;
	font-weight: @bold;
	line-height: 1em;
	display: none;
}

.table-search {
	margin: 5px 10px 10px;
	padding: 0px 10px 10px 10px;
	border: 1px solid @borderColor;
	background-color: @bgGrey;
	border-radius: 5px;

	.search-title {
		line-height: 1em;
		margin-top: 12px;
		font-size: 16px;
	}

	.input-wrap {
		width: 100%;
		display: inline-block;
		margin: 10px 10px 0 0;
		position: relative;
		vertical-align: top;

		&.number {
			width: 100px;
		}

		&.button,
		&.switcher {
			width: auto;
		}

		label {
			display: block;
			font-size: 12px;
			margin-bottom: 2px;

			.tip {
				margin-left: 4px;
				top: -2px;
			}
		}

		.large-button {
			padding: 5px 8px;
			font-size: 14px;
			margin-top: 2px;
		}
	}

	input {
		outline: none;
		font-size: 14px;
		border-radius: 3px;

		&[type="text"],
		&[type="number"] {
			border: 1px solid rgba(0, 0, 0, 0.1);
			padding: 5px;
			width: 200px;
			height: 34px;
			vertical-align: top;
			width: 100%;
		}

		&[type="number"] {
			width: 80px;
		}
	}

	.search-actions {
		margin-top: 0;
		display: inline-block;
		vertical-align: top;
		padding-top: 30px;
		padding-bottom: 10px;

		&.multiple {
			padding-top: 10px;
			padding-bottom: 0;

			&.has-simple {
				padding-top: 30px;
			}

			> span {
				cursor: pointer;
				.opacity(0.5);

				svg {
					margin-right: 5px;
				}

				&:hover {
					.opacity(1);
				}
			}
		}
	}

	.mag {
		width: 20px;
		height: 20px;
		.opacity(0.5);
		cursor: pointer;
		display: inline-block;
		margin-right: 10px;

		&:hover {
			.opacity(1);
		}
	}

	.csv,
	.pdf,
	.save {
		width: 20px;
		height: 20px;
		.opacity(0.5);
		cursor: pointer;
		display: inline-block;
		margin-right: 10px;

		&:hover {
			.opacity(1);
		}
	}

	.clearSearch {
		display: inline-block;
		font-size: 14px;
		color: @formDefaultColour;
		cursor: pointer;
		vertical-align: top;
		padding-top: 2px;
		margin-right: 10px;
	}

	.v-select {
		display: inline-block;
		width: 100%;
		vertical-align: top;
		font-family: inherit;
		font-size: 14px;
		background: white;

		.vs__search {
			font-family: inherit;
			font-size: 14px;
			margin: 6px 0 0;
			color: rgba(0, 0, 0, 0.55);
		}

		.vs__dropdown-toggle,
		.vs__dropdown-menu {
			border-color: rgba(0, 0, 0, 0.1);
			border-radius: 0;
		}

		.vs__dropdown-toggle {
			padding: 0 0 5px;
		}
	}
}

&.no-bg-search {
	.table-search {
		padding: 0;
		border: none;
		background: none;
	}
}

.table-wrap-wrap {
	.table-wrap {
		margin-top: 20px;

		&:first-child {
			margin-top: 0;
		}
	}
}

.table-mobile {
	.table-title {
		&.show {
			display: block;
		}
	}

	.select-all {
		text-align: left;
		padding: 10px 14px;
	}

	.table-item {
		padding: 15px 10px 5px;
		border-bottom: 2px solid @borderColor;

		&.highlighted {
			background: url("./../assets/img/icons/star-solid.svg") 10px 17px no-repeat;
			background-size: 18px auto;

			.table-item-wrap {
				&:first-child {
					.table-cell {
						padding-left: 30px;
					}
				}
			}
		}
	}

	.table-item-wrap {
		display: inline;
		position: static;
	}

	&:last-child {
		border-bottom: none;
	}

	&:nth-child(even) {
		background-color: @bgGrey;
	}

	.show-more {
		font-size: 12px;
		margin-bottom: 10px;
		font-weight: @bold;
		text-align: center;
		cursor: pointer;
		display: block;
	}

	.table-cell {
		margin-bottom: 10px;
		position: relative;

		&.third {
			display: inline-block;
			width: 32%;
			text-align: center;
		}

		&.hide-initially {
			display: none;
		}

		.cell-heading,
		.cell-value {
			text-align: left;
			font-size: 12px;
			word-wrap: break-word;
			margin: 0;
			display: inline-block;
			vertical-align: top;
		}

		.cell-heading {
			font-weight: @bold;
			margin-right: 5px;
			vertical-align: middle;

			&:after {
				content: ": ";
			}
		}

		.cell-value {
			font-size: 16px;
			font-weight: normal;

			&.checkbox {
				padding-left: 16px;
			}

			span {
				&.clickable {
					cursor: pointer;

					&:hover {
						color: @formDefaultColour;
					}

					a {
						color: inherit;

						&:hover {
							color: inherit;
						}
					}
				}
			}
		}
	}

	&.show-all {
		.table-cell {
			&.hide-initially {
				display: block;
			}
		}
	}
}

.table-wrap-sticky {
	display: inline;
	position: relative;
}

table {
	border-collapse: collapse;
	width: 100%;

	tr {
		border-top: 1px solid @borderColor;

		&.highlighted {
			background: url("./../assets/img/icons/star-solid.svg") 6px 15px no-repeat;
			background-size: 18px auto;
		}

		&:first-child {
			border-top: none;
		}

		&:nth-child(even) {
			background-color: fade(@bgGrey, 65%);
		}

		&.no-bg {
			background: none;
		}

		&.add-button {
			background: none;
			td {
				padding-top: 20px;
				padding-bottom: 20px;
			}
		}

		&.filter {
			background: none;

			td {
				padding-top: 15px;

				.v-select {
					width: 200px;
				}
			}
		}

		th,
		td {
			text-align: left;
			width: auto;
			font-size: 14px;
			font-weight: normal;
			vertical-align: top;
			position: relative;
			max-width: 50vw;

			&:first-child {
				padding-left: 5px;
			}

			input {
				margin-top: 13px;
			}

			&.hide {
				display: none;
			}

			&.checkBox {
				width: 30px;
				vertical-align: middle;

				input {
					margin-top: 8px;
				}
			}

			&.center {
				text-align: center;

				span.cell {
					padding-left: 5px;
					padding-right: 5px;
				}
			}

			&.green {
				color: @green;
			}

			&.red {
				color: @red;
			}

			.recheck {
				color: @red;
				font-size: 12px;
				cursor: pointer;
			}

			.link {
				color: @red;
				cursor: pointer;
				.trans;
				&:hover {
					color: #000;
				}
			}

			&.sort {
				.cell {
					cursor: pointer;

					&:after {
						content: "\2195";
						display: inline;
						padding-left: 5px;
					}
				}
			}

			&.sorted {
				.cell {
					&:after {
						content: "\2193";
						color: @green;
					}
				}

				&.desc {
					.cell {
						&:after {
							content: "\2191";
						}
					}
				}
			}

			.edit {
				display: block;
				cursor: pointer;
				color: @formDefaultColour;
				font-size: 14px;
				line-height: 1em;
				.opacity(0);

				&.has-discs {
					left: 0;
					top: 0;
					width: 100%;
					height: 100%;
					z-index: 2;
					position: absolute;
					padding-left: 6px;
				}

				&.show {
					.opacity(1);
				}

				span {
					display: block;
					width: 100%;
					text-align: left;
					padding-top: 4px;
				}
			}

			&.editable {
				&:hover {
					.edit {
						.opacity(1);
					}

					.discs {
						.opacity(0);
					}
				}
			}

			> span {
				&:not(.large-button):not(.status) {
					padding: 14px 10px 13px 0px;
				}

				display: inline-block;

				&.discs {
					padding-bottom: 0;
					min-height: 20px;
				}

				&.empty {
					color: @formDefaultColour;
					padding-top: 15px;
				}
			}

			.clickable {
				cursor: pointer;

				&.red {
					color: @red;
				}

				&:hover {
					color: @formDefaultColour;
				}

				a {
					color: inherit;
					text-decoration: none;

					&:hover {
						color: inherit;
					}
				}
			}

			&.col-1 {
				width: 10%;
			}

			&.col-1-12 {
				width: 12%;
			}

			&.col-2 {
				width: 20%;
			}

			&.col-3 {
				width: 30%;
			}

			&.col-4 {
				width: 40%;
			}

			&.col-5 {
				width: 50%;
			}

			&.col-6 {
				width: 60%;
			}

			&.col-7 {
				width: 70%;
			}

			&.col-8 {
				width: 80%;
			}

			&.col-9 {
				width: 90%;
			}

			&.col-10 {
				width: 100%;
			}
		}

		th {
			border-bottom: 1px solid rgba(0, 0, 0, 0.05);
			font-weight: 500;
			font-size: 16px;
			color: rgba(0, 0, 0, 0.6);
			vertical-align: bottom;

			&.checkBox {
				vertical-align: bottom;
			}

			&.pad {
				width: 20px;
			}

			> span {
				padding-top: 5px;

				&.hasTip {
					text-decoration: underline;
					text-decoration-style: dashed;
					cursor: help;
					position: relative;
				}
			}
		}

		td {
			color: rgba(0, 0, 0, 0.7);
			vertical-align: middle;
			&.checkbox {
				span {
					padding: 0 !important;
				}
			}
		}

		&.disabled {
			td {
				.opacity(0.5);

				&.ignore-disabled {
					.opacity(1);
				}
			}
		}

		&.selected {
			background-color: rgba(0, 0, 0, 0.4);

			td {
				color: #fff;

				.red,
				.edit,
				.link {
					color: #fff;
				}

				.link {
					&:hover {
						color: #000;
					}
				}

				.disc {
					&.white {
						background-color: #fff;
						color: #000;
					}
				}
			}
		}
	}
}

.has-mobile {
	table {
		display: none;
	}
}

@media screen and (min-width: 768px) {
	.show-headings {
		margin-bottom: 5px;
		position: relative;
		z-index: 2;
		padding: 6px 10px;
		left: 10px;
		display: inline-block;
		margin: 0 0px 10px;

		.show-headings-heading {
			cursor: pointer;
			margin-bottom: 0;
			font-size: 14px;
			text-align: center;
			display: inline-block;
		}

		.show-headings-wrap {
			display: none;

			.show-headings-sub-heading {
				color: #fff;
			}
		}
		&:not(.show-filters) {
			.trans;
			margin-top: 10px;

			&:hover {
				background-color: @formDefaultColour;
			}
		}
	}

	&.show-filters {
		padding: 10px;
		width: auto;
		left: auto;
		border-radius: 5px 5px 0 0;
		margin: 10px;
		display: block;
		z-index: 4;

		.show-headings-wrap {
			display: block;
		}

		.show-headings-heading {
			margin-bottom: 5px;
			font-size: 16px;
			text-align: left;

			&:hover {
				&:after {
					content: "hide section";
					display: inline-block;
					padding-left: 15px;
					font-size: 0.7em;
				}
			}
		}
	}
}

@media screen and (min-width: 960px) {
	.the-query {
		margin: 10px 14px 0px !important;
	}

	.show-headings {
		left: 14px;
		text-align: left;
		margin-bottom: 0;

		&.show-filters {
			margin: 10px 14px 0;
			padding: 10px 15px 5px;
		}
	}

	.sticky-btn {
		display: block;
	}

	.table-title {
		padding: 12px 30px 0;
		display: block;
		text-align: left;
	}

	.showing-filters {
		.table-title {
			padding-top: 14px;
		}
		.sticky-btn {
			top: -25px;
		}
	}

	.table-search {
		margin: 15px 14px 10px;
		padding-right: 60px;

		.input-wrap {
			width: 250px;

			&.dates {
				width: 200px;
			}
		}

		.v-select {
			width: 250px;
		}
	}

	.has-mobile {
		.table-mobile {
			display: none;
		}

		table {
			display: table;
		}

		.table-wrap-sticky {
			&.active {
				overflow: auto;
				position: fixed;
				width: 100%;
				height: 100%;
				z-index: 999;
				top: 0;
				left: 0;
				background: white;
				bottom: 0;
				display: block;
				box-sizing: border-box;
				padding-right: 35px;

				table {
					position: relative;
					th {
						background: white;
						position: sticky;
						top: 0; /* Don't forget this, required for the stickiness */
						box-shadow: 0 2px 2px -1px rgba(0, 0, 0, 0.4);
						z-index: 4;
					}
				}
			}
		}
	}

	.table-wrap {
		padding: 0;
		overflow-x: auto;

		[type="checkbox"]:not(old) + label:before,
		.table-wrap [type="radio"]:not(old) + label:before {
			left: 7px;
		}
	}
}
</style>
